<template>
  <div>
    <transition
      mode="out-in"
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 translate-y-2"
      leave-active-class="transition duration-200 ease-in"
      leave-to-class="opacity-0 translate-y-2"
    >
    <!-- Main Hydration Status Bar -->
    <status-bar
      v-if="
        (loadingIndustryPartners ||
          question.industryPartnersStatus?.isLoading) &&
        !loadedIndustryPartners
      "
      key="loading"
      :total-step-count="totalStepCount"
      :current-step-number="currentStepNumber"
      :status="status || question.industryPartnersStatus?.message"
      :substatus="substatus"
      :began-at="question.industryPartnersStatus?.beganAt"
      theme="amber"
      @cancel="$emit('cancel-loading', 'industryPartners')"
    />
    <div
      v-else-if="!loadedIndustryPartners"
      key="load-action"
      class="text-center w-full py-8"
    >
      <!-- Reusable Decoupled Error Card -->
      <research-compass-error-card
        v-if="isFailed"
        title="Industry Partner Search Failed"
        description="An unexpected error occurred while identifying candidate industry partners."
        button-text="Retry Partner Search"
        button-class="bg-amber-600 hover:bg-amber-700 dark:bg-amber-500 dark:hover:bg-amber-600"
        @retry="getIndustryPartners()"
      />

      <!-- Standard Load Industry Partners Action -->
      <template v-else>
        <button
          class="bg-amber-600 hover:bg-amber-700 dark:bg-amber-500 dark:hover:bg-amber-600 text-white px-6 py-2.5 uppercase text-sm font-bold rounded-xl transition-all shadow-lg flex items-center mx-auto gap-2"
          @click="getIndustryPartners()"
        >
          <i class="fas fa-building"></i>
          Load Industry Partners
        </button>
        <div
          class="mt-4 flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 text-xs font-semibold italic"
        >
          <i class="fa-regular fa-clock-three text-amber-500/70"></i>
          Usually takes about 3 minutes
        </div>
      </template>
    </div>
    <div v-else-if="loadedIndustryPartners" key="content">
      <div class="mb-8 min-h-[100px]">
        <!-- Explanation (Summary) View -->
        <div v-if="selectedSubTab === 'summary'">
          <div v-if="narrative && !generatingNarrative" class="mb-6">
            <div class="flex justify-end mb-2 gap-3">
              <button
                v-if="
                  !question.industryPartners ||
                  question.industryPartners.length === 0
                "
                class="text-[10px] font-bold uppercase tracking-wider text-gray-500 hover:text-amber-600 transition-colors flex items-center gap-1.5"
                @click="getIndustryPartners()"
              >
                <i class="fas fa-arrows-rotate"></i>
                Retry Industry Partner Search
              </button>
              <button
                class="text-[10px] font-bold uppercase tracking-wider text-gray-500 hover:text-amber-600 transition-colors flex items-center gap-1.5"
                @click="clearNarrative()"
              >
                <i class="fas fa-arrows-rotate"></i>
                Regenerate Summary
              </button>
            </div>
            <research-compass-markdown-viewer
              :text="narrative"
              :question="question"
              ref-name="companyHolder"
              @scroll-to-citation="scrollToPartner"
            />
            <div class="flex justify-end mt-2">
              <feedback-modal
                sentiment="POSITIVE"
                item-type="INDUSTRY_PARTNER_NARRATIVE"
                :item-id="question.hashedId"
                :ancillary-information-label="question.question"
                :research-compass-hashed-id="$route.params.researchCompassId"
              />
              <feedback-modal
                sentiment="NEGATIVE"
                item-type="INDUSTRY_PARTNER_NARRATIVE"
                :item-id="question.hashedId"
                :ancillary-information-label="question.question"
                :research-compass-hashed-id="$route.params.researchCompassId"
              />
            </div>
          </div>
          <div v-else-if="generatingNarrative" class="py-10 px-6">
            <status-bar
              :total-step-count="totalStepCount"
              :current-step-number="currentStepNumber"
              :status="status"
              :substatus="substatus"
              theme="amber"
            />
          </div>
          <div
            v-else
            class="py-10 text-center bg-gray-50/50 dark:bg-gray-900/30 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700"
          >
            <button
              class="bg-white dark:bg-gray-800 hover:bg-amber-50 dark:hover:bg-amber-900/30 border border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-400 px-6 py-2.5 uppercase text-xs font-bold rounded-xl transition-all shadow-sm flex items-center mx-auto gap-2"
              @click="generateNarrative()"
            >
              <i class="fas fa-wand-magic-sparkles"></i>
              Generate Partner Explanation
            </button>
            <div
              class="mt-4 flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 text-xs font-semibold italic"
            >
              <i class="fa-regular fa-clock-three text-amber-500/70"></i>
              Usually takes about a minute
            </div>
          </div>
        </div>

        <!-- Comparison (Table) View -->
        <div v-if="selectedSubTab === 'table'">
          <div v-if="generatingTable" class="py-10 px-6">
            <status-bar
              :total-step-count="totalStepCount"
              :current-step-number="currentStepNumber"
              :status="status"
              :substatus="substatus"
              theme="amber"
            />
          </div>
          <div
            v-else-if="!table"
            class="py-10 text-center bg-gray-50/50 dark:bg-gray-900/30 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700"
          >
            <div
              v-if="
                !question.industryPartners ||
                question.industryPartners.length === 0
              "
              class="text-center"
            >
              <i class="fas fa-info-circle text-gray-400 text-2xl mb-2"></i>
              <p class="font-bold text-gray-500 text-sm">
                No industry partners available to compare.
              </p>
            </div>
            <button
              v-else
              class="bg-white dark:bg-gray-800 hover:bg-amber-50 dark:hover:bg-amber-900/30 border border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-400 px-6 py-2.5 uppercase text-xs font-bold rounded-xl transition-all shadow-sm flex items-center mx-auto gap-2"
              @click="generateTable()"
            >
              <i class="fas fa-wand-magic-sparkles"></i>
              Generate Comparison Table
            </button>
            <div
              class="mt-4 flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 text-xs font-semibold italic"
            >
              <i class="fa-regular fa-clock-three text-amber-500/70"></i>
              Usually takes about a minute
            </div>
          </div>
          <div v-else class="overflow-x-auto pb-4 custom-scrollbar">
            <research-compass-markdown-viewer
              :text="table"
              :question="question"
              ref-name="companyHolder"
              :show-table-scroll-indicators="true"
              @scroll-to-citation="scrollToPartner"
            />
            <div class="flex justify-end mt-4">
              <feedback-modal
                sentiment="POSITIVE"
                item-type="INDUSTRY_PARTNER_TABLE"
                :item-id="question.hashedId"
                :ancillary-information-label="question.question"
                :research-compass-hashed-id="$route.params.researchCompassId"
              />
              <feedback-modal
                sentiment="NEGATIVE"
                item-type="INDUSTRY_PARTNER_TABLE"
                :item-id="question.hashedId"
                :ancillary-information-label="question.question"
                :research-compass-hashed-id="$route.params.researchCompassId"
              />
            </div>
          </div>
        </div>

        <!-- List View -->
        <div v-if="selectedSubTab === 'list'" class="w-full">
          <div
            v-if="
              !question.industryPartners ||
              question.industryPartners.length === 0
            "
            class="py-10 text-center bg-gray-50/50 dark:bg-gray-900/30 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700"
          >
            <div class="text-center mb-6">
              <i class="fas fa-info-circle text-gray-400 text-3xl mb-3"></i>
              <p class="font-bold text-gray-600 dark:text-gray-400 text-sm">
                No potential industry partners were found for this query.
              </p>
              <p class="text-gray-500 dark:text-gray-500 text-xs mt-2">
                Click below to try the search again.
              </p>
            </div>
            <button
              class="bg-white dark:bg-gray-800 hover:bg-amber-50 dark:hover:bg-amber-900/30 border border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-400 px-6 py-2.5 uppercase text-xs font-bold rounded-xl transition-all shadow-sm flex items-center mx-auto gap-2"
              @click="getIndustryPartners()"
            >
              <i class="fas fa-arrows-rotate"></i>
              Retry Industry Partner Search
            </button>
          </div>
          <template v-else>
            <div
              v-for="(partner, index) in question.industryPartners"
              :key="partner.name"
              :ref="`partner${index}`"
              class="bg-white/60 dark:bg-[#0d1a20]/60 backdrop-blur-xl border border-gray-300 dark:border-gray-700 shadow-xl rounded-2xl p-5 mb-4 transition-all duration-300 hover:shadow-2xl scroll-mt-[150px]"
            >
              <div class="w-full flex justify-between items-start mb-4">
                <div class="flex items-start w-full">
                  <div class="shrink-0">
                    <img
                      v-if="partner.logoUrl"
                      :src="partner.logoUrl"
                      class="h-12 w-12 object-contain mr-4 rounded-xl bg-white dark:bg-transparent p-1 border border-gray-200 dark:border-gray-700 dark:grayscale dark:invert dark:opacity-90"
                    />
                    <div
                      v-else
                      class="h-12 w-12 bg-gray-100 dark:bg-gray-800 flex items-center justify-center mr-4 rounded-xl border border-gray-200 dark:border-gray-700"
                    >
                      <i
                        class="fa-solid fa-building text-gray-400 dark:text-gray-500 text-xl"
                      />
                    </div>
                  </div>
                  <div class="w-full">
                    <div class="w-full flex justify-between items-start">
                      <a
                        v-if="partner.validatedUrl"
                        :href="partner.validatedUrl"
                        target="_blank"
                        class="group"
                      >
                        <h4
                          class="text-lg font-headline font-bold text-gray-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors flex items-center gap-2"
                        >
                          <span v-html="partner.name" />
                          <i
                            class="fa-solid fa-arrow-up-right-from-square text-[10px] opacity-0 group-hover:opacity-100 transition-opacity"
                          ></i>
                        </h4>
                      </a>
                      <h4
                        v-else
                        class="text-lg font-headline font-bold text-gray-900 dark:text-white"
                        v-html="partner.name"
                      />
                      <div class="flex items-start gap-1 mt-1">
                        <feedback-modal
                          sentiment="POSITIVE"
                          item-type="INDUSTRY_PARTNER"
                          :item-id="question.hashedId"
                          :ancillary-information="partner.validatedUrl"
                          :ancillary-information-label="partner.name"
                          :research-compass-hashed-id="
                            $route.params.researchCompassId
                          "
                        />
                        <feedback-modal
                          sentiment="NEGATIVE"
                          item-type="INDUSTRY_PARTNER"
                          :item-id="question.hashedId"
                          :ancillary-information="partner.validatedUrl"
                          :ancillary-information-label="partner.name"
                          :research-compass-hashed-id="
                            $route.params.researchCompassId
                          "
                        />
                      </div>
                    </div>
                    <div class="flex flex-wrap items-center gap-2 mt-2 mb-3">
                      <span
                        v-if="partner.type_"
                        class="bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800/50 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
                        v-html="partner.type_"
                      />
                      <span
                        v-if="partner.employeeCount"
                        class="bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-gray-200 px-2 py-0.5 rounded text-[10px] font-bold flex items-center shadow-sm"
                      >
                        <i class="fas fa-users mr-1.5 opacity-60"></i>
                        <span v-html="partner.employeeCount" />
                      </span>
                      <span
                        v-if="partner.hqLocation"
                        class="bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-gray-200 px-2 py-0.5 rounded text-[10px] font-bold flex items-center shadow-sm"
                      >
                        <i class="fas fa-map-marker-alt mr-1.5 opacity-60"></i>
                        <span v-html="partner.hqLocation" />
                      </span>
                      <span
                        v-if="partner.annualRevenue"
                        class="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/50 px-2 py-0.5 rounded text-[10px] font-bold flex items-center shadow-sm"
                      >
                        <i class="fas fa-dollar-sign mr-1.5 opacity-60"></i>
                        <span v-html="partner.annualRevenue" />
                      </span>
                      <span
                        v-if="partner.foundedYear"
                        class="bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-gray-200 px-2 py-0.5 rounded text-[10px] font-bold flex items-center shadow-sm"
                      >
                        <i class="fas fa-calendar-day mr-1.5 opacity-60"></i>
                        <span class="mr-1">Founded</span>
                        <span v-html="partner.foundedYear" />
                      </span>
                    </div>

                    <div
                      class="bg-amber-500/5 dark:bg-amber-400/5 border-l-4 border-amber-500/50 dark:border-amber-400/30 rounded-r-lg px-5 py-4 mt-6 mb-4 text-sm text-gray-700 dark:text-gray-300 italic shadow-sm"
                    >
                      <div
                        class="text-[10px] font-bold uppercase tracking-widest text-amber-600/70 dark:text-amber-400/70 mb-2 flex items-center"
                      >
                        <i class="fas fa-sparkles mr-2"></i>
                        Why they fit
                      </div>
                      <div v-html="partner.suitability" />
                    </div>

                    <div
                      class="text-sm text-gray-600 dark:text-gray-400 leading-relaxed"
                    >
                      <div
                        class="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-2 flex items-center"
                      >
                        <i class="fas fa-info-circle mr-2"></i>
                        About
                      </div>
                      <div v-html="partner.description" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
    </transition>
  </div>
</template>

<script>
import * as utils from "../../utils";
import StatusBar from "./StatusBar.vue";
import FeedbackModal from "./FeedbackModal.vue";
import ResearchCompassMarkdownViewer from "./ResearchCompassMarkdownViewer.vue";
import ResearchCompassErrorCard from "./ResearchCompassErrorCard.vue";

const { resolveRcApiUrl } = utils;

export default {
  components: {
    StatusBar,
    FeedbackModal,
    ResearchCompassMarkdownViewer,
    ResearchCompassErrorCard,
  },
  props: {
    question: Object,
    loadedIndustryPartners: Boolean,
    loadingIndustryPartners: Boolean,
    selectedSubTab: String,
  },
  emits: [
    "start-loading",
    "finish-loading",
    "cancel-loading",
    "reload-research-compass",
    "change-sub-tab",
    "get-industry-partners",
    "update:generating-narrative",
    "update:generating-table",
  ],
  data() {
    return {
      totalStepCount: 0,
      currentStepNumber: 0,
      status: "",
      substatus: "",
      researchCompassQuestionChannel: null,
      requestedPartners: [],
      generatingNarrative: false,
      generatingTable: false,
    };
  },
  computed: {
    isFailed() {
      const status = this.question.industryPartnersStatus;
      return !!(status?.hasError || status?.message === "Error");
    },
    narrative() {
      const answers = this.question.answers || [];
      const narrative = answers
        .filter(
          (answer) =>
            (answer.answerType?.value || answer.answerType) ===
            "INDUSTRY_PARTNER_NARRATIVE",
        )
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      return narrative.length > 0 ? narrative[0].content : null;
    },
    table() {
      const answers = this.question.answers || [];
      const table = answers
        .filter(
          (answer) =>
            (answer.answerType?.value || answer.answerType) ===
            "INDUSTRY_PARTNER_TABLE",
        )
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      return table.length > 0 ? table[0].content : null;
    },
    isPublic() {
      return Boolean(this.$route.params.viewKey);
    },
    activeAxios() {
      if (this.isPublic) {
        return this.$api.axiosPublic;
      }
      return this.$api.axios;
    },
  },
  watch: {
    generatingNarrative(newVal) {
      this.$emit("update:generating-narrative", newVal);
    },
    generatingTable(newVal) {
      this.$emit("update:generating-table", newVal);
    },
    narrative(newVal) {
      if (newVal) {
        this.generatingNarrative = false;
      }
    },
    table(newVal) {
      if (newVal) {
        this.generatingTable = false;
      }
    },
  },
  mounted() {
    this.setUpChannel(this.question.hashedId);
  },
  methods: {
    setUpChannel(hashedId) {
      if (this.researchCompassQuestionChannel === null) {
        const channel = this.$ably.channels.get(
          `${
            import.meta.env.VITE_ENV_CONFIG_NAMESPACE || ""
          }researchcompassquestion${hashedId}industrypartners`,
        );
        const self = this;
        channel.subscribe((message) => {
          let data;
          try {
            data =
              typeof message.data === "string"
                ? JSON.parse(message.data)
                : message.data;
          } catch (e) {
            console.error("Failed to parse websocket message", e);
            return;
          }

          if (!data) return;

          // Some notifications (like RamyAssist) nest their data in metadata
          const statusData = data.metadata || data;

          if (statusData.status !== "Error") {
            if (statusData.status) self.status = statusData.status;
            if (statusData.substatus !== undefined)
              self.substatus = statusData.substatus;
            if (!self.loadedIndustryPartners) {
              self.$emit("start-loading", "industryPartners");
            }
            if (statusData.totalStepCount)
              self.totalStepCount = statusData.totalStepCount;
            if (statusData.currentStepNumber)
              self.currentStepNumber = statusData.currentStepNumber;
            if (statusData.reload) {
              self.$emit("reload-research-compass");
            }
            if (statusData.finished) {
              // We do NOT set generating flags to false here anymore.
              // The watchers will handle it when the data actually arrives.
              if (!self.loadedIndustryPartners) {
                self.$emit("finish-loading", "industryPartners");
              }
            }
          } else {
            // Handle error state
            self.generatingNarrative = false;
            self.generatingTable = false;
            if (statusData.reload) {
              self.$emit("reload-research-compass");
            }
            self.$emit("cancel-loading", "industryPartners");
          }
        });
        this.researchCompassQuestionChannel = channel;
      }
    },
    getIndustryPartners() {
      this.status = "Initializing industry partner hydration...";
      this.substatus = "";
      this.totalStepCount = 10;
      this.currentStepNumber = 1;
      this.$emit("get-industry-partners");
    },
    requestMeeting(partner) {
      this.requestedPartners.push(partner.name);
    },
    scrollToPartner(partnerName) {
      this.$emit("change-sub-tab", "list");
      this.$nextTick(() => {
        const index = (this.question.industryPartners || []).findIndex(
          (p) => p.name.toLowerCase() === partnerName.toLowerCase(),
        );

        if (index !== -1) {
          const refId = `partner${index}`;
          const el = this.$refs[refId];
          const target = Array.isArray(el) ? el[0] : el;
          const targetEl = target?.$el || target;
          if (targetEl) {
            targetEl.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }
      });
    },
    generateNarrative() {
      this.generatingNarrative = true;
      this.status = "Initializing explanation...";
      this.substatus = "";
      this.totalStepCount = 10;
      this.currentStepNumber = 1;
      const url = resolveRcApiUrl(
        this.question.hashedId,
        "generate-industry-partner-narrative",
        this.$route.params,
      );

      this.activeAxios.post(url).catch(() => {
        this.generatingNarrative = false;
      });
    },
    clearNarrative() {
      const url = resolveRcApiUrl(
        this.question.hashedId,
        "clear-industry-partner-narrative",
        this.$route.params,
      );

      this.activeAxios.post(url).then(() => {
        this.$emit("reload-research-compass");
        this.generateNarrative();
      });
    },
    generateTable() {
      this.generatingTable = true;
      this.status = "Initializing comparison...";
      this.substatus = "";
      this.totalStepCount = 10;
      this.currentStepNumber = 1;
      const url = resolveRcApiUrl(
        this.question.hashedId,
        "generate-industry-partner-table",
        this.$route.params,
      );

      this.activeAxios.post(url).catch(() => {
        this.generatingTable = false;
      });
    },
  },
};
</script>
